import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Stack,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Rating,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Star as StarIcon,
  Park as ParkIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';
import { dogGardensApi } from '../services/api';
import { imageApi } from '../services/imageApi';
import LocationPickerMap from './LocationPickerMap';
import { normalizeImageUrl } from '../services/imageUtils';
import { alpha } from '@mui/material/styles';
import {
  Avatar,
  ListItemAvatar,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  People as PeopleIcon,
  Pets as PetsIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

const DogGardenDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [garden, setGarden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletePhotoId, setDeletePhotoId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [togglingPhotoId, setTogglingPhotoId] = useState(null);
  const [reorderingPhotos, setReorderingPhotos] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [loadingFollowers, setLoadingFollowers] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingFollowers(true);
    dogGardensApi.getFollowers(id)
      .then((data) => { if (!cancelled) { setFollowers(Array.isArray(data) ? data : []); setLoadingFollowers(false); } })
      .catch(() => { if (!cancelled) setLoadingFollowers(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Removal reports (newest first). Loaded once on mount; the user-visible "Removal reports"
  // section renders open + resolved reports inline.
  const [removalReports, setRemovalReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingReports(true);
    dogGardensApi.getRemovalReports(id)
      .then((data) => { if (!cancelled) { setRemovalReports(Array.isArray(data) ? data : []); setLoadingReports(false); } })
      .catch((err) => {
        console.error('Failed to load removal reports:', err);
        if (!cancelled) setLoadingReports(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const fetchGardenDetails = async () => {
      try {
        setLoading(true);
        const data = await dogGardensApi.getGardenById(id);
        setGarden(data);
        setError('');
      } catch (err) {
        console.error('Error fetching garden details:', err);
        setError('Failed to load garden details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchGardenDetails();
    }
  }, [id]);

  const handleBackClick = () => {
    navigate('/dog-gardens');
  };

  const handleVisibilityToggle = async () => {
    try {
      setLoading(true);

      // Create updated garden data with toggled visibility
      const updatedGarden = {
        ...garden,
        visible: !(garden.visible === true)
      };

      // Update garden
      await dogGardensApi.updateGarden(id, updatedGarden);

      // Refresh garden details
      const refreshedGarden = await dogGardensApi.getGardenById(id);
      setGarden(refreshedGarden);

      setSuccessMessage(`Garden visibility has been ${refreshedGarden.visible ? 'enabled' : 'disabled'}.`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error updating garden visibility:', err);
      setError(`Failed to update garden visibility: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploadingImage(true);

      // Upload image using imageApi
      const uploadResponse = await imageApi.uploadImage(selectedFile);

      if (!uploadResponse) {
        throw new Error('Failed to upload image');
      }

      // Add the photo to the garden using the new API endpoint
      await dogGardensApi.addPhotoToGarden(id, uploadResponse.largeUrl);

      // Refresh garden details
      const refreshedGarden = await dogGardensApi.getGardenById(id);
      setGarden(refreshedGarden);

      setSuccessMessage('Image uploaded successfully');
      setSelectedFile(null);

      // Reset file input
      const fileInput = document.getElementById('garden-image-upload');
      if (fileInput) {
        fileInput.value = '';
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error uploading image:', err);
      setError(`Failed to upload image: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      setLoading(true);

      // Delete the photo using the new API endpoint
      await dogGardensApi.removePhotoFromGarden(id, photoId);

      // Refresh garden details
      const refreshedGarden = await dogGardensApi.getGardenById(id);
      setGarden(refreshedGarden);

      setSuccessMessage('Photo deleted successfully');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error deleting photo:', err);
      setError(`Failed to delete photo: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePhotoVisibility = async (photoId) => {
    try {
      setTogglingPhotoId(photoId);

      // Toggle the photo visibility using the API endpoint
      const updatedPhoto = await dogGardensApi.togglePhotoVisibility(id, photoId);

      // Refresh garden details
      const refreshedGarden = await dogGardensApi.getGardenById(id);
      setGarden(refreshedGarden);

      setSuccessMessage(`Photo visibility ${updatedPhoto.visible ? 'enabled' : 'disabled'}`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error toggling photo visibility:', err);
      setError(`Failed to toggle photo visibility: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setTogglingPhotoId(null);
    }
  };

  // Move a photo to a new position in the gallery (position 0 = cover image).
  // Sends the full id order to the backend, then refreshes with the saved order.
  const handleMovePhoto = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= garden.photos.length || fromIndex === toIndex) {
      return;
    }

    const orderedPhotoIds = garden.photos.map((photo) => photo.id);
    const [movedId] = orderedPhotoIds.splice(fromIndex, 1);
    orderedPhotoIds.splice(toIndex, 0, movedId);

    try {
      setReorderingPhotos(true);

      await dogGardensApi.reorderGardenPhotos(id, orderedPhotoIds);

      // Refresh garden details
      const refreshedGarden = await dogGardensApi.getGardenById(id);
      setGarden(refreshedGarden);

      setSuccessMessage('Photo order updated');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error reordering photos:', err);
      setError(`Failed to reorder photos: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setReorderingPhotos(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick}>Back to Dog Gardens</Button>
        </Stack>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!garden) {
    return (
      <Container sx={{ mt: 4 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick}>Back to Dog Gardens</Button>
        </Stack>
        <Alert severity="warning">Garden not found</Alert>
      </Container>
    );
  }

  // The admin endpoint returns hidden photos too — the hero must be a visible one
  const heroPhoto = garden.photos?.find((photo) => photo.visible !== false);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>Dashboard</Button>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick}>Back to Dog Gardens</Button>
      </Stack>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {heroPhoto ? (
              <Card>
                <CardMedia
                  component="img"
                  height="250"
                  image={normalizeImageUrl(heroPhoto.photoUrl)}
                  alt={garden.gardenName}
                  sx={{ objectFit: 'cover' }}
                />
              </Card>
            ) : (
              <Box 
                sx={{ 
                  height: 250, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'grey.200',
                  borderRadius: 1
                }}
              >
                <ParkIcon sx={{ fontSize: 80, color: 'grey.400' }} />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" component="h1">
                {garden.gardenName}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={garden.visible === true}
                    onChange={handleVisibilityToggle}
                    color="primary"
                  />
                }
                label={garden.visible ? "Visible" : "Hidden"}
              />
            </Box>

            {garden.rating && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating value={garden.rating} precision={0.5} readOnly />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({garden.userRatingTotal || 0} reviews)
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    {garden.address}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    {garden.cityDto?.nameEn ?? garden.cityDto?.nameHe ?? garden.cityName ?? '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationIcon />
                  </ListItemIcon>
                  <Typography
                    variant="body2"
                    component="a"
                    href={`https://www.google.com/maps?q=${garden.latitude},${garden.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {garden.latitude}, {garden.longitude}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CalendarIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    Created: {garden.createdAt ? new Date(garden.createdAt).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <UpdateIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    Updated: {garden.updatedAt ? new Date(garden.updatedAt).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <VisibilityIcon />
                  </ListItemIcon>
                  <Typography variant="body2">
                    Visible: {garden.visible !== null && garden.visible !== undefined ? garden.visible.toString() : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PublicIcon />
                  </ListItemIcon>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">Source:</Typography>
                    {garden.source ? (
                      <Chip
                        size="small"
                        label={garden.source}
                        color={garden.source === 'USER' ? 'primary' : 'default'}
                        variant={garden.source === 'USER' ? 'filled' : 'outlined'}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PersonIcon />
                  </ListItemIcon>
                  <Typography variant="body2" component="div">
                    Submitted by:{' '}
                    {garden.submittedBy ? (
                      <MuiLink
                        component={RouterLink}
                        to={`/users/${garden.submittedBy.id}`}
                        underline="hover"
                      >
                        {garden.submittedBy.name}
                      </MuiLink>
                    ) : (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {garden.source === 'SYSTEM' ? 'System import' : '—'}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Location Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Location
        </Typography>
        <LocationPickerMap
          latitude={garden.latitude}
          longitude={garden.longitude}
          readOnly
          height={250}
        />
      </Paper>

      {/* Image Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Upload Image
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              type="file"
              id="garden-image-upload"
              onChange={handleFileChange}
              fullWidth
              inputProps={{
                accept: 'image/*'
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUploadImage}
              disabled={!selectedFile || uploadingImage}
              startIcon={<AddPhotoIcon />}
            >
              {uploadingImage ? 'Uploading...' : 'Upload Image'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Gallery Section */}
      {garden.photos && garden.photos.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Gallery
          </Typography>

          <Grid container spacing={2}>
            {garden.photos.map((photo, index) => (
              <Grid item xs={6} sm={4} md={3} key={photo.id || index}>
                <Card sx={{ position: 'relative' }}>
                  {index === 0 && (
                    <Chip
                      icon={<StarIcon />}
                      label="Cover"
                      size="small"
                      color="primary"
                      sx={{ position: 'absolute', top: 8, left: 8 }}
                    />
                  )}
                  <CardMedia
                    component="img"
                    height="150"
                    image={normalizeImageUrl(photo.photoUrl)}
                    alt={`${garden.gardenName} image ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {photo.visible ? (
                        <Tooltip title="Visible">
                          <VisibilityIcon color="primary" fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Hidden">
                          <VisibilityOffIcon color="disabled" fontSize="small" />
                        </Tooltip>
                      )}
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        {photo.visible ? 'Visible' : 'Hidden'}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleTogglePhotoVisibility(photo.id)}
                        disabled={togglingPhotoId === photo.id}
                        sx={{ mr: 1 }}
                      >
                        {togglingPhotoId === photo.id ? (
                          <CircularProgress size={20} />
                        ) : photo.visible ? (
                          <Tooltip title="Hide photo">
                            <VisibilityOffIcon />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Show photo">
                            <VisibilityIcon />
                          </Tooltip>
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeletePhoto(photo.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ px: 1, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Tooltip title="Move earlier">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleMovePhoto(index, index - 1)}
                            disabled={reorderingPhotos || index === 0}
                          >
                            <ChevronLeftIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move later">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleMovePhoto(index, index + 1)}
                            disabled={reorderingPhotos || index === garden.photos.length - 1}
                          >
                            <ChevronRightIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Tooltip title="Set as cover image">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleMovePhoto(index, 0)}
                          disabled={reorderingPhotos || index === 0}
                        >
                          <StarIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
      {/* Removal Reports Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ bgcolor: alpha('#ED6C02', 0.1), color: '#ED6C02', width: 36, height: 36, borderRadius: 1.5 }}>
            <ReportProblemIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant="h6" fontWeight={600}>
            Removal reports
          </Typography>
          {!loadingReports && (() => {
            const openCount = removalReports.filter((r) => r.status === 'OPEN').length;
            return (
              <Stack direction="row" spacing={1}>
                {openCount > 0 && (
                  <Chip label={`${openCount} open`} size="small" color="warning" />
                )}
                <Chip
                  label={`${removalReports.length} total`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            );
          })()}
        </Box>

        {loadingReports ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : removalReports.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No users have reported this garden as removed.
          </Typography>
        ) : (
          <List dense>
            {removalReports.map((r) => (
              <ListItem key={r.id} alignItems="flex-start" divider sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip
                        size="small"
                        label={r.reason}
                        color={r.status === 'OPEN' ? 'warning' : 'default'}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={r.status}
                        color={r.status === 'OPEN' ? 'warning' : 'success'}
                      />
                      <Typography variant="body2" color="text.secondary">
                        by{' '}
                        <MuiLink
                          component={RouterLink}
                          to={`/users/${r.reporterId}`}
                          underline="hover"
                        >
                          {r.reporterName}
                        </MuiLink>
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <>
                      {r.note && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          "{r.note}"
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {new Date(r.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Followers Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ bgcolor: alpha('#6D28D9', 0.1), color: '#6D28D9', width: 36, height: 36, borderRadius: 1.5 }}>
            <PeopleIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant="h6" fontWeight={600}>
            Followers
          </Typography>
          {!loadingFollowers && (
            <Chip
              label={followers.length}
              size="small"
              sx={{ bgcolor: alpha('#6D28D9', 0.1), color: '#6D28D9', fontWeight: 700, fontSize: '0.75rem' }}
            />
          )}
        </Box>

        {loadingFollowers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : followers.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No followers yet.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {followers.map((profile, idx) => {
              const user = profile.user;
              if (!user) return null;
              const initials = (user.name || user.email || 'U').charAt(0).toUpperCase();
              const location = [user.city, user.country].filter(Boolean).join(', ');
              const dogCount = (profile.dogs || []).length;
              return (
                <React.Fragment key={profile.id || idx}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItem
                    sx={{
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha('#6D28D9', 0.04) },
                      px: 1,
                    }}
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={normalizeImageUrl(user.picture)}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: alpha('#6D28D9', 0.12),
                          color: '#6D28D9',
                          fontWeight: 700,
                        }}
                      >
                        {initials}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {user.name || user.email}
                        </Typography>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          {user.email && user.name && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          )}
                          {location && (
                            <Typography component="span" variant="caption" color="text.disabled">
                              {location}
                            </Typography>
                          )}
                          {dogCount > 0 && (
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                              <PetsIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                              <Typography component="span" variant="caption" color="text.disabled">
                                {dogCount} dog{dogCount !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="View user">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); navigate(`/users/${user.id}`); }}
                          sx={{ color: 'text.disabled' }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default DogGardenDetails;
